/**
 * client/src/reader/useNarration.ts — the wire between the reader and the audio engine.
 *
 * Everything on both sides of this file was built and tested in isolation and neither could reach the
 * other: `AudioEngine` constructs no URLs and guesses no page ids ("both are the reader's to answer,
 * not the engine's"), and `Reader` takes a `ReaderNarration` plug it was never handed. This is the
 * plug. It owns exactly four things and deliberately nothing else:
 *
 *   1. **Which page a sentence is on.** A page id (`c003.p07`) is local to its edition (LAW 1), so
 *      the mapping is one indexed lookup over this edition's own page list and never a guess from a
 *      chapter number.
 *   2. **How a page id becomes an artifact.** `POST /pages/:pageId/narration/ensure` — the one door
 *      §4.8 describes — validated through `validatePageAudio` before anything downstream sees it.
 *   3. **Reading order across chapters**, which is what `pagesAfter` means and why the engine cannot
 *      answer it.
 *   4. **Turning engine events into React state**, once, at 2 Hz-ish rather than 60 — the highlight
 *      rides `frames` and never goes through React at all.
 *
 * ⚠️ **`frames` is not optional in practice.** `useCueSync` will start its own rAF loop if this hook
 * does not hand it the engine's tick subscription, and two loops is two wakeups per frame on a locked
 * phone for hours — the exact cost P2's gate is measured against. It is passed unconditionally below.
 *
 * ⚠️ **Nothing here is a millisecond.** Seeks travel as `{sid, frac}` (LAW 3), pages are identified by
 * `(pageId, artifactSha)`, and the only `ms` this file touches is `chapter.pageOffsetMs`, which comes
 * from the server's §4.6 prefix sums and is advisory. If you find yourself adding a number of seconds
 * to a page id here, that is the bug §2 exists to prevent.
 *
 * ⚠️ **The chapter framing comes from the server, not from the artifact.** `chapterOffsetSec` and
 * `chapterDurationSec` are §4.6's prefix sums (`chapterAxisFor`), sent beside the audio. Computing
 * them from this page's own `durationMs` would be individually correct and would produce §5.7's named
 * defect exactly: a lock screen showing a 90-second track that restarts at every page boundary. It
 * arrives through the data, not through the code, which is why the fields are carried rather than
 * derived.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { compareAddress } from "../../../shared/address.ts";
import type { PageState } from "../../../shared/narration/cue.ts";
import { cueIndexAt, fracInCue, validatePageAudio } from "../../../shared/narration/cue.ts";
import { audioEngine } from "../audio/AudioEngine.ts";
import type { EngineEvent, EngineStatus, PageResolution } from "../audio/AudioEngine.ts";
import { locateChapterMs, type PageAxis } from "../audio/chapterSeek.ts";
import type { EnginePage } from "../audio/pageWindow.ts";
import type { SleepChoice } from "../audio/sleepTimer.ts";
import { cachedBookResponse, getOfflineBook } from "../lib/idb.ts";
import { loadRate, loadTrackButtons, saveRate, saveTrackButtons, TRACK_SKIP_SEC, type TrackButtons } from "../lib/listenPrefs.ts";
import { derivedOrd, makePosition, saveLocal } from "../lib/position.ts";
import type { ListenControls } from "./ListenView.tsx";
import type { ReaderNarration } from "./Reader.tsx";

// ─── what the page index looks like on the wire ──────────────────────────────

/**
 * ⚠️ **No `state` field, and the route is asked not to compute one.**
 *
 * `GET /books/:id/pages` used to run a full narration plan for EVERY page in the book — ~9 indexed
 * statements, a sha256 per sentence and an `fs` stat each, measured at 1.21 ms/page (126 ms for
 * Alice's 104; ~500 ms for Pride and Prejudice; seconds for a Bible) — synchronously, on cloud-2's
 * event loop, which is the same thread serving audio Range requests to a locked phone. This hook is
 * the only caller that fetches the whole book (it needs reading order for `pagesAfter`) and it never
 * read the field. Per-page state comes from `GET /pages/:pageId/narration`, which is about one page.
 */
interface PageRow {
  id: string;
  ord: number;
  chapterOrd: number;
  /** `sentences.globalOrd` of the page's first sentence — the `ord` a `listen` position sends. */
  startOrd?: number;
  startSid: string;
  endSid: string;
  role: string;
}

/** What `ensure` and the plan GET both answer with. Read defensively — this is a network boundary. */
interface EnsureBody {
  state?: PageState;
  plan?: {
    state?: PageState;
    missingChars?: number;
    estGpuSec?: number;
    blocked?: { code?: string; message?: string } | null;
  } | null;
  /**
   * ⚠️ §4.11's swap watcher polls the plan `GET` for THIS and compares `artifactSha`, not `state`.
   * After a regeneration the page is not "a page that is missing" — it is "a page whose artifact is
   * about to become a different one", and `state: "ready"` cannot tell those apart, because the OLD
   * artifact is still ready and still a correct rendering of the old text. The sha is what moves.
   */
  audio?: unknown;
  chapter?: { chapterOrd?: number; pageOffsetMs?: number; durationMs?: number; title?: string | null } | null;
  etaSec?: number | null;
  error?: string;
  code?: string;
}

/** What we remember about a page between polls. Drives the stall copy and `overdue`. */
interface PageNote {
  state: PageState;
  missingChars: number | null;
  etaSec: number | null;
  /** When the wait started, so "about 12 seconds" can become "still going" honestly. */
  askedAt: number;
  /** `blocked.code === "nothing-to-say"`: a full-page plate. Never narrated, and never waited on. */
  silent: boolean;
  /**
   * The planner's reason, carried so the reader can SAY it.
   *
   * ⚠️ Without this the whole offline path is mute. `ensure` answers 503 with "Karti's voice is
   * offline right now…", `narration.ts` words it carefully, and it reached exactly nothing: the hook
   * dropped it and `WaitingBanner` — which has an `offline` tone written for this case — was never
   * mounted. During a spark-1 outage a reader saw a play button that did nothing.
   */
  blocked: { code?: string | null; message?: string | null } | null;
}

export interface NarrationBook {
  bookId: string;
  editionId: string;
  title: string;
  author: string | null;
  hasCover: boolean;
  /** Whose voice. The follow pill says "Karti is on page 14" and a `default` book must not. */
  voiceLabel: string;
}

/**
 * The reader's narration plug, or `null` when there is nothing to plug in.
 *
 * `null` — rather than an object with every handler disabled — when there is no audio engine (no DOM,
 * i.e. `node --test`) or no edition yet. `Reader` renders byte-for-byte P1 in that case, which is the
 * property that keeps an un-narrated book from paying for any of this.
 */
/**
 * The cold-path poll ladder, in milliseconds. Deliberately the same shape as `AudioEngine`'s
 * `WAIT_POLL_MS` — one page is ~21–30 s of GPU (§4.8) and a queue of one is normal, so it starts
 * fast and backs off rather than hammering the planner.
 */
const POLL_MS = [1_200, 2_000, 3_500, 6_000, 8_000];

/**
 * How long a page's `ensure` may take before we stop waiting for the network and ask the copy on the
 * device. Without it a dead zone — a tunnel, the far end of a car park — hangs the request for as
 * long as the OS feels like, and the saved copy that was there the whole time is never asked.
 */
export const PAGE_FETCH_TIMEOUT_MS = 6_000;

/**
 * `ensure` gets longer: it may probe spark-1 (up to 15 s, cached 10 s) before answering, and aborting
 * that at 6 s threw away the one answer — `tts-unreachable` — that lets the engine say the voice
 * service is down. Dead zones are still caught by this, just later; the plan GET keeps the 6 s.
 */
export const ENSURE_TIMEOUT_MS = 20_000;

/** `AbortSignal.timeout`, where it exists (Safari 16+). Absent, the request is simply unbounded. */
function timeoutSignal(ms: number): AbortSignal | undefined {
  const AS = AbortSignal as unknown as { timeout?: (ms: number) => AbortSignal };
  return typeof AS.timeout === "function" ? AS.timeout(ms) : undefined;
}

export interface NarrationHandlers {
  /**
   * The last page of the book has finished playing and the engine has paused itself.
   *
   * Handled by the **page**, not by this hook: continuing means either seeking this book back to its
   * first sid or navigating to another book, and only `Read.tsx` holds a router. `client/src/lib/
   * continuous.ts` is the policy; this is the wire.
   *
   * ⚠️ Held in a ref below and never in the subscribe effect's dependency list. `Read.tsx` builds this
   * closure inline on every render, so a dependency on it would tear down and re-subscribe the engine
   * listener on each one — and the engine is a document-lifetime singleton, so the window between the
   * unsubscribe and the resubscribe is a window in which `end-of-book` reaches nobody. That event
   * fires exactly once per book and is not repeated.
   */
  onEndOfBook?: (() => void) | null;
  /**
   * The room composes this plug and owns the speed (`onRate` is routed through it), so a book's
   * remembered speed must not be applied over the room's.
   */
  inRoom?: boolean;
}

export function useNarration(book: NarrationBook | null, handlers?: NarrationHandlers): ReaderNarration | null {
  const engine = audioEngine;
  const handlersRef = useRef<NarrationHandlers | undefined>(handlers);
  handlersRef.current = handlers;
  const inRoom = handlers?.inRoom === true;

  const [pages, setPages] = useState<PageRow[]>([]);
  const [status, setStatus] = useState<EngineStatus>("idle");
  const [enginePage, setEnginePage] = useState<EnginePage | null>(null);
  const [, forceTick] = useState(0);

  /**
   * Everything the engine's callbacks read is a ref, and that is structural rather than tidy.
   *
   * `setSource` is called **once**; the object it is given lives for the life of the engine, which is
   * the life of the document. If its closures captured `pages` or `book` by value, the engine would
   * spend the whole session answering "which page comes after this one" from the empty array it was
   * handed on the first render — and `pagesAfter` returning `[]` is indistinguishable from the end of
   * the book, so the audio would simply stop at the first page boundary with no error anywhere.
   */
  const pagesRef = useRef<PageRow[]>([]);
  pagesRef.current = pages;
  const bookRef = useRef<NarrationBook | null>(book);
  bookRef.current = book;
  const notes = useRef(new Map<string, PageNote>());
  /** Each page's place on its chapter's axis, as the server last stated it. `chapterSeek.ts` reads it. */
  const axes = useRef(new Map<string, PageAxis>());
  const anchorRef = useRef<string | null>(null);
  /**
   * "Somebody pressed play on a page that did not exist yet."
   *
   * `undefined` means nobody is waiting; a value (including `null`, meaning "wherever the reader is")
   * is the sid to start from the moment the planner says `ready`. Stored as a ref rather than state
   * because nothing renders it and it must survive the re-render the poll itself causes.
   */
  const intendPlay = useRef<string | null | undefined>(undefined);
  /**
   * The page the reader is looking at, as **state** — unlike `anchorRef`, which is the sid.
   *
   * A page id changes a handful of times a minute (a page turn), not per frame, so making this state
   * costs one render per turn and buys the effect below. The *sid* stays a ref for exactly the reason
   * `ReaderNarration.onAnchor` documents: it changes on every landed relayout and nothing renders it.
   */
  const [anchorPageId, setAnchorPageId] = useState<string | null>(null);
  /**
   * Bumped when somebody asks for a page that is not ready. It is a dependency of the poll effect
   * below and nothing else, and it exists because a ref cannot restart an effect: the poll for this
   * page had already run and stopped (the page was `missing`), and pressing Narrate is precisely the
   * event that makes it worth asking again.
   */
  const [pollNonce, setPollNonce] = useState(0);

  /* ── the page index ────────────────────────────────────────────────────── */

  useEffect(() => {
    // Offsets belong to an edition's axis (LAW 1); a new book or edition starts with none.
    axes.current.clear();
    if (book === null) {
      setPages([]);
      return;
    }
    const ac = new AbortController();
    void (async () => {
      try {
        const res = await fetch(`/api/v1/books/${encodeURIComponent(book.bookId)}/pages`, {
          credentials: "same-origin",
          headers: { accept: "application/json" },
          signal: ac.signal,
        });
        if (!res.ok) return;
        const body = (await res.json()) as { pages?: PageRow[] };
        if (ac.signal.aborted || !Array.isArray(body.pages)) return;
        // Sorted here rather than trusted from the route: `pagesAfter` IS reading order, and a list
        // that arrives out of order would play chapter 9 after chapter 3 with everything reporting
        // success. The route does order it; this costs one sort per book and removes the question.
        setPages([...body.pages].sort((a, b) => a.ord - b.ord));
      } catch {
        // A failed index is an un-narratable book, not a broken reader. The play button will say so
        // through `state: "missing"` rather than this hook throwing into a render.
      }
    })();
    return () => ac.abort();
  }, [book?.bookId, book?.editionId]);

  /* ── sid → page, the only mapping in this file ─────────────────────────── */

  /**
   * The page containing a sentence, by its own boundaries.
   *
   * `compareAddress` and not a string compare: sids are `c003.b0007.s2` and a lexicographic test
   * gets `b0010` vs `b0009` right only by the zero padding, which is exactly the kind of correctness
   * that survives until the day someone changes `BLOCK_PAD`. The comparator is the one every other
   * ordering in the product uses, and it throws on an unparseable id rather than sorting it to the
   * front.
   */
  const pageOfSid = useCallback((sid: string | null): PageRow | null => {
    if (sid === null) return null;
    const list = pagesRef.current;
    for (const p of list) {
      try {
        if (compareAddress(p.startSid, sid) <= 0 && compareAddress(p.endSid, sid) >= 0) return p;
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  /* ── the engine source: reading order, and how a page becomes bytes ─────── */

  const resolvePage = useCallback(
    async (pageId: string): Promise<PageResolution> => {
      const b = bookRef.current;
      if (b === null) return { ok: false, state: "missing", etaSec: null, message: "This book is not open." };

      const now = Date.now();
      const q = new URLSearchParams({ bookId: b.bookId, editionId: b.editionId });
      const planUrl = `/api/v1/pages/${encodeURIComponent(pageId)}/narration?${q}`;

      // ⚠️ A book saved for offline asks the device FIRST — in a dead zone, network-first costs a
      // timeout at every page boundary before the copy in hand is used. Only a playable plan counts.
      let body: EnsureBody | null = (await isSavedOffline(b, pageId)) ? await cachedPlan(planUrl, true) : null;

      if (body === null) {
        try {
          const res = await fetch(`/api/v1/pages/${encodeURIComponent(pageId)}/narration/ensure`, {
            method: "POST",
            credentials: "same-origin",
            headers: { "content-type": "application/json", accept: "application/json" },
            body: JSON.stringify({ bookId: b.bookId, editionId: b.editionId }),
            signal: timeoutSignal(ENSURE_TIMEOUT_MS),
          });
          body = (await res.json().catch(() => ({}))) as EnsureBody;
        } catch (err) {
          /**
           * ⚠️ **P6, §5.8: with no network — or a network that stopped answering — ask the READ half.**
           *
           * `ensure` is a POST — it may queue GPU — so no cache can answer it and no service worker
           * may claim it. Its read half is `GET /pages/:id/narration`, which the worker's `ab-book-v1`
           * cache holds for every page a downloaded book warmed. Without this fallback, **a book that
           * was downloaded in full refuses to play in airplane mode**: the plan says `ready`, the
           * artifact is on the device, the play button is enabled, and pressing it produces silence
           * and a poll ladder retrying a POST that cannot succeed. That is §12's entire P6 gate
           * failing with nothing in any log — measured in a real Chrome with `setOffline(true)` before
           * this branch existed.
           *
           * Its cached copy is read directly first — the worker is network FIRST, so in a dead zone
           * a `fetch` of the GET hangs exactly as the POST just did. Read half only: nothing here can
           * start a job, so an un-narrated page offline still comes back `missing` and still waits.
           */
          body = await cachedPlan(planUrl, false);
          if (body === null) {
            const fallback = await fetch(planUrl, {
              credentials: "same-origin",
              headers: { accept: "application/json" },
              signal: timeoutSignal(PAGE_FETCH_TIMEOUT_MS),
            }).catch(() => null);
            if (fallback === null || !fallback.ok) {
              // The network, not the narration. Reported as `missing` (retried) and never `failed`
              // (terminal) — a dropped request during a poll ladder must not end the book with a
              // banner promising a page that is on its way.
              return { ok: false, state: "missing", etaSec: null, message: (err as Error).message };
            }
            body = (await fallback.json().catch(() => ({}))) as EnsureBody;
          }
        }
      }

      const plan = body.plan ?? null;
      const axis = body.chapter ?? null;
      if (axis !== null && typeof axis.pageOffsetMs === "number" && typeof axis.durationMs === "number") {
        axes.current.set(pageId, { offsetMs: axis.pageOffsetMs, chapterDurationMs: axis.durationMs });
      }
      // ⚠️ The refusal's code is TOP-LEVEL, not under `plan` — a 503 has no plan, because nothing was
      // planned. `narration.ts` answers `{error, code: "tts-unreachable"}` when spark-1 is
      // unreachable, and reading only `plan.blocked` dropped it on the floor: `WaitingBanner` has an
      // `offline` tone written for exactly that code and nothing could ever reach it.
      const blockedCode = plan?.blocked?.code ?? body.code ?? null;
      const state: PageState = body.state ?? plan?.state ?? "missing";
      const note: PageNote = {
        state,
        missingChars: typeof plan?.missingChars === "number" ? plan.missingChars : null,
        etaSec: typeof body.etaSec === "number" ? body.etaSec : (plan?.estGpuSec ?? null),
        askedAt: notes.current.get(pageId)?.askedAt ?? now,
        silent: blockedCode === "nothing-to-say",
        /**
         * The planner's reason when it planned; the refusal's own when it refused.
         *
         * ⚠️ **And it is STICKY while the state does not change**, which is the half that makes it
         * reach a reader. The poll ladder alternates: `ensure` answers 503 with
         * `code: "tts-unreachable"` and no `plan.blocked`, then the very next `GET …/narration`
         * answers a perfectly ordinary plan whose `blocked` is null. A non-sticky field is therefore
         * overwritten with null a second after it is set, and the banner flickers back to the
         * generic "this page hasn't been narrated yet" — which is exactly what a reader saw
         * throughout the 2026-08-07 spark-1 outage.
         *
         * Sticky only *within* a state. `note.askedAt` below already resets when `state` moves, and
         * a reason that outlived its state would be the opposite failure: "offline" printed over a
         * page that has started playing.
         */
        blocked:
          plan?.blocked ??
          (body.code ? { code: body.code, message: body.error ?? null } : null) ??
          (notes.current.get(pageId)?.state === state ? (notes.current.get(pageId)?.blocked ?? null) : null),
      };
      // A page whose state changed has a new clock: "about 12 seconds" that has been on screen for
      // two minutes is worse than no estimate, and `overdue` is how the bar says so.
      if (notes.current.get(pageId)?.state !== state) note.askedAt = now;
      notes.current.set(pageId, note);
      forceTick((n) => n + 1);

      if (note.silent) {
        // Not a failure and not a wait. `pagesAfter` skips it from here on and the engine's poll
        // ladder re-asks for the next page 1.2 s later, which is what makes this self-correcting
        // rather than a special case the engine has to know about.
        return { ok: false, state: "missing", etaSec: null, message: plan?.blocked?.message, silent: true };
      }

      const check = validatePageAudio(body.audio);
      if (!check.ok) {
        /**
         * LAW 9, at the only boundary that can both refuse the page and put the refusal on screen.
         *
         * A `?? 0` on `leadInMs` here would make `masterMs()` NaN, freeze the highlight for a whole
         * page, and — because `NaN > tolerance` is false — silently disable the desync guard that
         * exists to catch precisely this. The refusal is a wait, not an error: the overwhelmingly
         * common reason `audio` is absent is that the GPU has not made it yet.
         */
        /**
         * ⚠️ **Only the PLANNER may say `failed`.** `failed` is terminal — `AudioEngine.waitForNext`
         * stops the wait on it and puts a banner and a manual Retry on screen — and a 5xx is not the
         * planner speaking. A 502 from Caddy during a reload, a 503 from the `lane-unserved` branch
         * while the worker restarts, spark-1 rebooting: every one of them ended the book. §4.9's
         * design for exactly this is a backoff ladder with a three-minute deadline, which the engine
         * already has; a transient server error belongs on the ladder, not past it.
         */
        return { ok: false, state, etaSec: note.etaSec, message: body.error ?? plan?.blocked?.message, code: blockedCode };
      }

      const ch = body.chapter ?? null;
      const row = pagesRef.current.find((p) => p.id === pageId) ?? null;
      /**
       * ⚠️ **No fallback for the chapter framing, on purpose** (LAW 9 again, and §5.7).
       *
       * The tempting default is `chapterDurationSec: durationMs / 1000`, which type-checks, renders,
       * and gives every listener a lock screen whose track is one page long and restarts every two
       * minutes. Refusing instead means a server that has not built its axis yet produces a page that
       * waits — visible, with a reason — rather than a product that quietly stops being an audiobook.
       */
      if (ch === null || typeof ch.pageOffsetMs !== "number" || typeof ch.durationMs !== "number") {
        return {
          ok: false,
          state,
          etaSec: note.etaSec,
          message: "This page has audio but no chapter timeline yet.",
        };
      }

      const page: EnginePage = {
        audio: check.page,
        chapterOrd: ch.chapterOrd ?? row?.chapterOrd ?? 0,
        chapterTitle: ch.title ?? `Chapter ${ch.chapterOrd ?? row?.chapterOrd ?? 0}`,
        chapterOffsetSec: ch.pageOffsetMs / 1000,
        chapterDurationSec: ch.durationMs / 1000,
      };
      return { ok: true, page };
    },
    [],
  );

  /**
   * ⚠️ **The engine is a document-lifetime singleton and this is where a book gives it back.**
   *
   * There is exactly one `AudioEngine`, constructed at module load, because both `<audio>` elements
   * must exist before the first gesture (iOS grants playback per element, inside one). So every book
   * in the session shares it, and nothing used to stop the previous one: start book A, tap back to
   * the shelf, open book B — A never stopped. `setSource` gets B's closures, but `decks[at].page` is
   * still A's page, so the play button takes the `currentPage !== null` branch and resumes **A**;
   * the lock screen still says A's chapter; `prepareAhead` keeps pulling A's pages over cellular.
   *
   * `close()` and deliberately **not** `destroy()`: `destroy()` sets a permanent flag on a singleton,
   * so the second reader you open in a session would be a dead engine that makes no sound and
   * returns `false` from everything.
   */
  useEffect(() => {
    if (engine === null) return;
    engine.setSource({
      pagesAfter: (pageId, n) => {
        const list = pagesRef.current;
        const i = list.findIndex((p) => p.id === pageId);
        if (i < 0) return [];
        const out: string[] = [];
        for (let j = i + 1; j < list.length && out.length < n; j++) {
          const row = list[j];
          if (row === undefined) continue;
          // A page with nothing to say is not a page to wait on. Learned from the first `ensure`
          // that hit it, so the very first pass may still ask — and the poll ladder re-asks, which
          // is why one wasted round trip is the whole cost.
          if (notes.current.get(row.id)?.silent === true) continue;
          out.push(row.id);
        }
        return out;
      },
      pageAudio: resolvePage,
    });
    return () => {
      engine.close();
      notes.current.clear();
      axes.current.clear();
    };
  }, [engine, resolvePage]);

  /* ── engine events → React state, and nothing at 60 Hz ─────────────────── */

  useEffect(() => {
    if (engine === null) return;
    return engine.subscribe((ev: EngineEvent) => {
      // `tick` is deliberately not handled. It fires every animation frame; putting it into React
      // state would reconcile ~2000 `<span data-s>` sixty times a second, forever, with the screen
      // off. The highlight rides `frames` below and never touches React.
      switch (ev.type) {
        case "status":
          setStatus(ev.status);
          break;
        case "page":
        case "resync":
          setEnginePage(ev.page);
          break;
        case "end-of-book":
          // The banner still needs the render; the continuation is policy and lives outside the hook.
          forceTick((n) => n + 1);
          handlersRef.current?.onEndOfBook?.();
          break;
        case "listen-point":
          saveListenRef.current();
          break;
        case "waiting":
        case "error":
        case "artifact-deferred":
        case "rate":
        case "needs-gesture":
        case "sleep":
          forceTick((n) => n + 1);
          break;
        default:
          break;
      }
    });
  }, [engine]);

  /**
   * The unlock and the resync, attached to the document once.
   *
   * `attachUnlockGesture` is what makes the *second* deck playable: iOS grants playback permission
   * per element, inside a gesture, and an element first touched at a page boundary was never granted
   * anything — it stalls silently exactly once, at the end of page one, which is the failure that
   * looks least like a permissions problem.
   */
  useEffect(() => {
    if (engine === null || typeof document === "undefined") return;
    const off = [engine.attachUnlockGesture(document), engine.attachVisibilityResync(document)];
    return () => {
      for (const f of off) f();
    };
  }, [engine]);

  useEffect(() => {
    if (engine === null || book === null) return;
    engine.setNowPlaying(
      { bookId: book.bookId, title: book.title, author: book.author, hasCover: book.hasCover },
      {
        seekToChapterSec: (sec) => seekChapterMs(sec * 1000),
        /**
         * ⚠️ **±15/30 s must be able to leave the page, or the buttons are dead exactly where they
         * are used.** The engine's own `skip()` clamps hard to the loaded artifact, because it cannot
         * fetch the next page's bytes synchronously inside a Media Session handler — so twenty
         * seconds from the end of a page, +30 s creeps a few seconds and stops, and a second press
         * does nothing at all. `mediaSession.ts` states the rule this broke in its own words: *"a dead
         * button is worse than an absent one"*, and iOS draws both buttons unconditionally. The
         * chapter axis is what this hook has and the engine does not, so the out-of-page case is
         * answered here, by `seekChapterMs`, which walks the pages' own offsets.
         */
        skip: (sec) => skipBy(sec),
        /**
         * The car's ⏭/⏮. Read from the setting at the moment of the press, so changing it needs no
         * re-binding: ±30 s by default (a steering-wheel button that jumps a chapter is an hour gone
         * with no screen to undo it), whole chapters for those who chose them.
         */
        nextChapter: () => (loadTrackButtons() === "chapter" ? jumpChapter(1) : skipBy(TRACK_SKIP_SEC)),
        previousChapter: () => (loadTrackButtons() === "chapter" ? jumpChapter(-1) : skipBy(-TRACK_SKIP_SEC)),
      },
    );
    // `seekChapterMs` is stable (see below) so this runs once per book rather than once per render.
  }, [engine, book?.bookId, book?.title, book?.author, book?.hasCover]);

  /**
   * A book's remembered speed, applied when it opens — unless a room owns the speed.
   *
   * Before the page index or any audio: `setRate` sets both decks' `defaultPlaybackRate`, so the
   * first page loads at the right speed rather than starting at 1× and jumping.
   */
  useEffect(() => {
    if (engine === null || book === null || inRoom) return;
    engine.setRate(loadRate(book.bookId));
  }, [engine, book?.bookId, inRoom]);

  /* ── the listen position ───────────────────────────────────────────────── */

  const lastListen = useRef<{ sid: string; at: number } | null>(null);

  /**
   * Write where the VOICE is, as a `listen` position — at each page boundary, on every pause, and
   * when the page is hidden while playing.
   *
   * The `read` position only moves when a page is turned on screen, and a locked phone turns no
   * pages: an hour of driving left the book an hour behind on the next open. `keepalive` because the
   * boundary handler runs under lock and `pagehide` runs as the tab goes away, and a plain `fetch`
   * from either is cancelled with the document. A failure is queued like any other position.
   */
  const saveListen = useCallback(() => {
    const b = bookRef.current;
    if (engine === null || b === null) return;
    const heard = engine.heardAt();
    // LAW 1: a sid goes nowhere without the edition it means something in, and only this one.
    if (heard === null || heard.page.audio.editionId !== b.editionId) return;
    const now = Date.now();
    const last = lastListen.current;
    // One pause fires twice (ours and the element's), so the same sentence inside a few seconds is one write.
    if (last !== null && last.sid === heard.sid && now - last.at < 5_000) return;

    const row = pagesRef.current.find((p) => p.id === heard.page.audio.pageId);
    // The page's first ordinal; the server replaces it with the sentence's own on a `listen` write.
    const ord = row?.startOrd ?? derivedOrd(heard.sid);
    if (ord === undefined) return;
    let pos;
    try {
      pos = makePosition({ sid: heard.sid, editionId: b.editionId, ord, kind: "listen", frac: heard.frac });
    } catch {
      return;
    }
    lastListen.current = { sid: heard.sid, at: now };
    saveLocal(b.bookId, pos);

    const wire = {
      kind: pos.kind,
      sid: pos.sid,
      ord: pos.ord,
      ms: pos.ms,
      frac: pos.frac,
      editionId: pos.editionId,
      chapterOrd: pos.chapterOrd,
      deviceId: pos.deviceId ?? null,
      clientUpdatedAt: pos.clientUpdatedAt,
    };
    const queue = (cause: "transport" | "server"): void =>
      void import("../lib/outbox.ts").then((m) =>
        m.enqueuePosition(b.bookId, "listen", pos.clientUpdatedAt, wire, `Where you were listening · ${b.bookId}`, cause),
      );
    void fetch(`/api/v1/books/${encodeURIComponent(b.bookId)}/position`, {
      method: "PUT",
      credentials: "same-origin",
      keepalive: true,
      headers: { "content-type": "application/json" },
      body: JSON.stringify(wire),
    })
      .then((res) => {
        if (res.status >= 500) queue("server");
      })
      .catch(() => queue("transport"));
  }, [engine]);
  // The engine subscription above is made once; it reads the writer through this.
  const saveListenRef = useRef(saveListen);
  saveListenRef.current = saveListen;

  useEffect(() => {
    if (engine === null || typeof document === "undefined") return;
    const onHide = (): void => {
      // Only while playing: a paused engine already wrote its place when it paused, and a reader
      // who has since browsed ahead on screen must not have that overwritten as the tab goes away.
      if (document.visibilityState === "hidden" && engine.playing) saveListenRef.current();
    };
    document.addEventListener("visibilitychange", onHide);
    addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      removeEventListener("pagehide", onHide);
    };
  }, [engine]);

  /* ── the handlers the reader calls ─────────────────────────────────────── */

  /** Open the page a sentence is on and start there. The only path from "reading" to "listening". */
  const playFrom = useCallback(
    async (sid: string | null): Promise<void> => {
      if (engine === null) return;
      const row = pageOfSid(sid ?? anchorRef.current);
      if (row === null) return;
      const res = await resolvePage(row.id);
      if (!res.ok) {
        // Nothing to open yet. The engine is left alone deliberately: `state` below is already
        // showing "warming up", and opening a page that does not exist would be an error banner over
        // a page that is generating perfectly well.
        //
        // ⚠️ But the INTENT is remembered, and that is the whole of §4.9's "it auto-resumes on
        // `ready`" on the cold path. Without it, tapping "Narrate this page" started the GPU, wrote
        // "warming up — about 25 seconds", and then nothing ever asked again: `AudioEngine`'s poll
        // ladder only runs once a page is open, and the state poll below used to return early on
        // `queued`. The bar sat on that sentence until the tab was reloaded — which is the first five
        // minutes of every book that was not warmed by LAW 10, and every chapter after the third.
        intendPlay.current = sid ?? anchorRef.current;
        setPollNonce((n) => n + 1);
        forceTick((n) => n + 1);
        return;
      }
      intendPlay.current = null;
      const at = sid !== null && res.page.audio.cues.some((c) => c.sid === sid) ? { sid, frac: 0 } : undefined;
      await engine.open(res.page, { at, autoplay: true });
    },
    [engine, pageOfSid, resolvePage],
  );

  const onPlayPause = useCallback(() => {
    if (engine === null) return;
    // Nothing loaded, or the reader has walked to a different page than the one playing: both mean
    // "start from what I am looking at". `toggle()` only ever means "the page already on the deck".
    if (engine.currentPage === null) {
      void playFrom(anchorRef.current);
      return;
    }
    engine.toggle();
  }, [engine, playFrom]);

  const onSeek = useCallback(
    (to: { sid: string; frac: number }) => {
      if (engine === null) return;
      // LAW 3: a sentence and a fraction of it, resolved against the engine's own cue list. If the
      // target is not in the loaded page it is a page change, and only this hook knows which page.
      if (!engine.seekToSid(to.sid, to.frac)) void playFrom(to.sid);
    },
    [engine, playFrom],
  );

  /**
   * A page's place on its chapter's axis — what `chapterSeek.ts` walks. From what `ensure` already
   * told us when it can be; otherwise the plan GET, which is the read half and costs no GPU.
   */
  const axisOf = useCallback(async (pageId: string): Promise<PageAxis | null> => {
    const known = axes.current.get(pageId);
    if (known !== undefined) return known;
    const b = bookRef.current;
    if (b === null) return null;
    try {
      const q = new URLSearchParams({ bookId: b.bookId, editionId: b.editionId });
      const res = await fetch(`/api/v1/pages/${encodeURIComponent(pageId)}/narration?${q}`, {
        credentials: "same-origin",
        headers: { accept: "application/json" },
        signal: timeoutSignal(PAGE_FETCH_TIMEOUT_MS),
      });
      if (!res.ok) return null;
      const ch = ((await res.json()) as EnsureBody).chapter ?? null;
      if (ch === null || typeof ch.pageOffsetMs !== "number" || typeof ch.durationMs !== "number") return null;
      const axis = { offsetMs: ch.pageOffsetMs, chapterDurationMs: ch.durationMs };
      axes.current.set(pageId, axis);
      return axis;
    } catch {
      return null;
    }
  }, []);

  /** Bumped per cross-page seek, so a slow walk that finishes after a newer press is dropped. */
  const seekSeq = useRef(0);

  /**
   * A point on the chapter axis — a scrub, a skip, a lock-screen seek.
   *
   * Inside the loaded page it is an in-page seek. Outside it, the page that holds it is found by each
   * page's own `chapterOffsetSec` (`locateChapterMs`), spilling into the neighbouring chapter at
   * either edge, and opened at the right SENTENCE — the in-page millisecond resolved against that
   * page's own cue list (LAW 3). It used to move one page and start at its top, so a 30 s skip near a
   * page edge landed up to two minutes away.
   *
   * Declared before the `setNowPlaying` effect uses it and kept stable so that effect runs once per
   * book: a lock-screen scrubber that is re-bound on every render drops the drag somebody is in.
   */
  const seekChapterMs = useCallback(
    (ms: number) => {
      if (engine === null) return;
      const page = engine.currentPage;
      if (page === null) return;
      const inPage = ms - page.chapterOffsetSec * 1000;
      if (inPage >= 0 && inPage <= page.audio.durationMs) {
        seekSeq.current++;
        // `inPage` is element time; `seekMasterMs` is on the cue axis and adds `leadInMs` back itself.
        engine.seekMasterMs(inPage - page.audio.leadInMs);
        return;
      }
      const seq = ++seekSeq.current;
      const autoplay = engine.playing || engine.state === "waiting";
      void (async () => {
        const target = await locateChapterMs(pagesRef.current, page.audio.pageId, ms, axisOf);
        if (target === null || seq !== seekSeq.current) return;
        const res = await resolvePage(target.pageId);
        if (seq !== seekSeq.current) return;
        if (!res.ok) {
          // Not narrated yet: the cold path, which remembers the intent and starts when it lands.
          const row = pagesRef.current.find((p) => p.id === target.pageId);
          if (row !== undefined) void playFrom(row.startSid);
          return;
        }
        const cues = res.page.audio.cues;
        const master = target.inPageMs - res.page.audio.leadInMs;
        const i = cueIndexAt(cues, master);
        const cue = i < 0 ? undefined : cues[i];
        const at = cue === undefined ? undefined : { sid: cue.sid, frac: fracInCue(cue, master) };
        await engine.open(res.page, { at, autoplay });
      })();
    },
    [engine, playFrom, resolvePage, axisOf],
  );

  /** ±seconds from where the voice is, on the chapter axis — so it crosses pages and chapters. */
  const skipBy = useCallback(
    (sec: number) => {
      if (engine === null) return;
      const page = engine.currentPage;
      if (page === null) return;
      const at = engine.el.currentTime;
      seekChapterMs((page.chapterOffsetSec + (Number.isFinite(at) ? at : 0) + sec) * 1000);
    },
    [engine, seekChapterMs],
  );

  /**
   * §5.7's chapter-scoped `nexttrack`/`previoustrack`, resolved by sid rather than by arithmetic.
   *
   * Forward means the FIRST page of the next chapter that has one; backward means the first page of
   * this chapter unless we are already on it, which is the convention every audiobook player uses and
   * the only one that makes a double-press mean "the chapter before this one".
   */
  const jumpChapter = useCallback(
    (dir: 1 | -1) => {
      if (engine === null) return;
      const page = engine.currentPage;
      const list = pagesRef.current;
      const here = page === null ? (pageOfSid(anchorRef.current)?.id ?? null) : page.audio.pageId;
      const i = here === null ? -1 : list.findIndex((p) => p.id === here);
      if (i < 0) return;
      const ord = list[i]!.chapterOrd;

      if (dir < 0) {
        const first = list.find((p) => p.chapterOrd === ord);
        // More than a couple of seconds in, "previous" means the top of THIS chapter.
        const atTop = first !== undefined && first.id === here && (page?.chapterOffsetSec ?? 0) === 0;
        const target = atTop ? [...list].reverse().find((p) => p.chapterOrd < ord) : first;
        const start = target === undefined ? undefined : list.find((p) => p.chapterOrd === target.chapterOrd);
        if (start !== undefined) void playFrom(start.startSid);
        return;
      }
      const next = list.find((p) => p.chapterOrd > ord);
      if (next !== undefined) void playFrom(next.startSid);
    },
    [engine, pageOfSid, playFrom],
  );

  const onRate = useCallback(
    (rate: number) => {
      engine?.setRate(rate);
      const b = bookRef.current;
      if (b !== null && !inRoom) saveRate(b.bookId, engine?.playbackRate ?? rate);
    },
    [engine, inRoom],
  );

  /** The Listen view's and the bar's extra controls. Stable for the life of the engine. */
  const listen = useMemo<ListenControls | null>(() => {
    if (engine === null) return null;
    return {
      skip: (sec) => skipBy(sec),
      sleep: () => engine.sleepState,
      setSleep: (choice: SleepChoice | null) => engine.setSleep(choice),
      keepAwakeSupported: engine.wakeLock.supported,
      keepAwake: () => engine.wakeLock.enabled,
      setKeepAwake: (on: boolean) => void (on ? engine.wakeLock.enable() : engine.wakeLock.disable()),
      trackButtons: () => loadTrackButtons(),
      setTrackButtons: (m: TrackButtons) => saveTrackButtons(m),
    };
  }, [engine, skipBy]);

  const onRetry = useCallback(() => {
    if (engine === null) return;
    // Clear what we remember first: `retryNextPage` re-asks, and a cached `failed` note would make
    // the bar keep saying "this page couldn't be narrated" over a page that is now generating.
    notes.current.clear();
    axes.current.clear();
    engine.retryNextPage();
    if (engine.currentPage === null) void playFrom(anchorRef.current);
  }, [engine, playFrom]);

  const onNarrate = useCallback(() => {
    void playFrom(anchorRef.current);
  }, [playFrom]);

  const onAnchor = useCallback(
    (sid: string | null) => {
      anchorRef.current = sid;
      const row = pageOfSid(sid);
      setAnchorPageId((cur) => (cur === (row?.id ?? null) ? cur : (row?.id ?? null)));
    },
    [pageOfSid],
  );

  /** §5.6's "Play from here". The menu names a sentence; this hook is the only thing that knows which page it is on. */
  const onPlayFrom = useCallback(
    (sid: string) => {
      void playFrom(sid);
    },
    [playFrom],
  );

  /* ── §4.11: something changed what this page says ──────────────────────── */

  /**
   * The swap we owe, or `null`. A ref because nothing renders it and it must survive the re-render
   * that arming it causes; `swapNonce` is the state that actually restarts the watcher, for the same
   * reason `pollNonce` exists — a ref cannot restart an effect.
   */
  const swapPending = useRef<{ pageId: string; sha: string; at: { sid: string; frac: number } | null } | null>(null);
  const [swapNonce, setSwapNonce] = useState(0);

  /**
   * A regeneration, a take put back, a muted sentence, an applied pronunciation — anything that moved
   * what this book says out from under an artifact that is already on the deck.
   *
   * Nothing is invalidated here and nothing is spliced. `planPage()` recomputes every clip key live
   * from `(ttsText, voice, take)`, so the instant the server moved the pointer this page's
   * `artifactKey` became unnameable and the ordinary `narrate.page`/`stitch.page` job is already
   * rebuilding it. All this has to do is notice when the new one exists and put the listener back on
   * the same **sentence**.
   */
  const onChanged = useCallback(
    (r: { sids: string[] }) => {
      if (engine === null) return;

      const targets = new Set<string>();
      for (const sid of r.sids) {
        const row = pageOfSid(sid);
        if (row !== null) targets.add(row.id);
      }
      if (targets.size === 0) return;

      // Forget what we knew about them first. A cached `ready` note would leave the playback bar
      // promising, in words, the audio that is in the middle of being replaced.
      for (const id of targets) notes.current.delete(id);

      const open = engine.currentPage;
      if (open === null || !targets.has(open.audio.pageId)) {
        // Nothing on the deck changed. The engine goes through `resolvePage` — i.e. `ensure` — every
        // single time it opens a page, so it picks the new artifact up for free when it gets there.
        setPollNonce((n) => n + 1);
        return;
      }

      /**
       * ⚠️ **THE PLAYING PAGE IS NEVER SWAPPED** (§4.11). Refetching 300 KB and reassigning `src`
       * mid-sentence is an audible gap and, on iOS, a chance of losing the Now Playing session. A fix
       * *ahead* of the playhead lands when the engine reaches that page; a fix *behind* it lands the
       * next time anybody opens it. Only a paused reader gets the swap now — which is also the only
       * case where a human is sitting there waiting to hear whether their fix worked.
       */
      if (engine.playing) return;

      /**
       * Where, as a **sentence and a fraction of it** — LAW 3, resolved against our own cue list.
       * A seconds-based restore lands mid-word the moment a re-read changes a duration, which is
       * precisely the event this function exists to survive.
       */
      const ms = engine.masterMs();
      const cue = open.audio.cues.find((c) => ms >= c.start && ms < c.end) ?? null;
      swapPending.current = {
        pageId: open.audio.pageId,
        sha: open.audio.artifactSha,
        at:
          cue === null
            ? null
            : { sid: cue.sid, frac: Math.min(1, Math.max(0, (ms - cue.start) / Math.max(1, cue.end - cue.start))) },
      };
      setSwapNonce((n) => n + 1);
    },
    [engine, pageOfSid],
  );

  /**
   * The swap watcher. Polls the **plan GET** — never `ensure`, which would spend a quota token per
   * poll on a page somebody already paid for — until the artifact sha moves, then re-opens the page
   * at the sentence the listener was on.
   */
  useEffect(() => {
    const want = swapPending.current;
    if (engine === null || book === null || want === null) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (attempt: number): Promise<void> => {
      if (cancelled) return;
      if (attempt > POLL_MS.length * 4) {
        // §4.9's deadline. Giving up is SILENT on purpose: the page still plays, and what it plays is
        // a correct rendering of the text as it was. A banner about a regeneration that is merely slow
        // would be an alarm raised over a book somebody is reading.
        swapPending.current = null;
        return;
      }
      try {
        const q = new URLSearchParams({ bookId: book.bookId, editionId: book.editionId });
        const res = await fetch(`/api/v1/pages/${encodeURIComponent(want.pageId)}/narration?${q}`, {
          credentials: "same-origin",
          headers: { accept: "application/json" },
        });
        if (!cancelled && res.ok) {
          const body = (await res.json()) as EnsureBody;
          const check = validatePageAudio(body.audio);
          if (check.ok && check.page.artifactSha !== want.sha) {
            swapPending.current = null;
            notes.current.delete(want.pageId);
            const open = engine.currentPage;
            // Still paused, still on the same page? If the listener pressed play or walked off while
            // the GPU worked, the swap is DROPPED rather than forced — §4.11 again, and the next open
            // of that page picks it up anyway.
            if (!engine.playing && open !== null && open.audio.pageId === want.pageId) {
              // The sid must be in the NEW cue list. "Don't read this sentence" removes cues, and
              // seeking to a sid that no longer exists is how a restore lands on the wrong line.
              const at = want.at !== null && check.page.cues.some((c) => c.sid === want.at?.sid) ? want.at : undefined;
              const resolved = await resolvePage(want.pageId);
              if (resolved.ok && !cancelled) await engine.open(resolved.page, { at: at ?? undefined, autoplay: false });
            }
            forceTick((n) => n + 1);
            return;
          }
        }
      } catch {
        // A failed poll is a question nobody answered, not an answer. Ask again.
      }
      if (cancelled) return;
      timer = setTimeout(() => void tick(attempt + 1), POLL_MS[Math.min(attempt, POLL_MS.length - 1)] ?? 8_000);
    };

    void tick(0);
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
    // `swapNonce` is the trigger; the payload is the ref, which is deliberately not a dependency.
  }, [swapNonce, engine, book?.bookId, book?.editionId, resolvePage]);

  /**
   * Ask what the page the reader is looking at would cost — **without committing to it**.
   *
   * `GET /pages/:pageId/narration` is the read half of `ensure` and exists for exactly this: "so a
   * reader can render *about 12 seconds* beside a play button, and so a client can poll a page's state
   * without the poll itself being a commitment of GPU".
   *
   * ⚠️ Without this the playback bar has nothing to go on and renders **"Narrate this page"** over a
   * page that is already narrated — because the hook learns a page's state only from `resolvePage`,
   * and `resolvePage` is `ensure`, which only runs when somebody presses something. So a book that is
   * fully narrated offers a Narrate button on every page, and pressing it works, which is exactly the
   * kind of wrong that nobody files: it does the right thing, one tap later than it should.
   *
   * A GET, never `ensure`: this fires on every page turn, and a POST here would spend an `ensure`
   * token per turn and queue GPU for pages nobody asked to hear.
   */
  useEffect(() => {
    if (book === null || anchorPageId === null) return;

    const ac = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const ask = async (attempt: number): Promise<void> => {
      try {
        const q = new URLSearchParams({ bookId: book.bookId, editionId: book.editionId });
        const res = await fetch(`/api/v1/pages/${encodeURIComponent(anchorPageId)}/narration?${q}`, {
          credentials: "same-origin",
          headers: { accept: "application/json" },
          signal: ac.signal,
        });
        if (!res.ok || ac.signal.aborted) return;
        const body = (await res.json()) as { plan?: EnsureBody["plan"] };
        const plan = body.plan ?? null;
        if (plan?.state === undefined) return;
        const previous = notes.current.get(anchorPageId);
        notes.current.set(anchorPageId, {
          state: plan.state,
          missingChars: typeof plan.missingChars === "number" ? plan.missingChars : null,
          etaSec: plan.estGpuSec ?? null,
          // The clock belongs to the WAIT, not to the poll: re-stamping it every 2 s would make
          // `overdue` unreachable and the bar would promise "about 25 seconds" forever.
          askedAt: previous?.state === plan.state ? (previous.askedAt ?? Date.now()) : Date.now(),
          silent: plan.blocked?.code === "nothing-to-say",
          blocked: plan.blocked ?? null,
        });
        forceTick((n) => n + 1);

        /**
         * §4.9: *"It auto-resumes on `ready`."*
         *
         * This is the only poller on the cold path. `AudioEngine`'s own ladder lives inside
         * `waitForNext`, which is reachable only once a page is playing, so a page that was `queued`
         * when the listener pressed Narrate had nothing watching it at all — the estimate went stale
         * on screen and the audio never started, for the whole of the first listen of every book.
         */
        if (plan.state === "queued" || plan.state === "building") {
          if (attempt >= POLL_MS.length * 4) return; // §4.9's deadline, not a spinner forever
          const wait = POLL_MS[Math.min(attempt, POLL_MS.length - 1)] ?? 8_000;
          timer = setTimeout(() => void ask(attempt + 1), wait);
          return;
        }
        // Ready, and somebody asked for it before it existed. `playFrom` re-checks `ensure`, so the
        // race with the engine opening it itself resolves to a no-op rather than a double open.
        const wanted = intendPlay.current;
        if (plan.state === "ready" && wanted !== undefined) {
          intendPlay.current = null;
          if (engine?.currentPage == null) void playFrom(wanted);
        }
      } catch {
        // A failed poll leaves the bar exactly as it was. It is a question, not a commitment.
      }
    };

    void ask(0);
    return () => {
      ac.abort();
      if (timer !== null) clearTimeout(timer);
    };
  }, [book?.bookId, book?.editionId, anchorPageId, engine, playFrom, pollNonce]);

  /* ── what the reader is handed ─────────────────────────────────────────── */

  /**
   * Which page the bar is about.
   *
   * The page on the deck when something is playing; otherwise **the page the reader is looking at**.
   * Those differ constantly — a listener browses ahead while the voice stays put (LAW 12), and the
   * Play button then has to be about the page under their eyes, because that is where pressing it
   * will start.
   */
  const currentId = enginePage?.audio.pageId ?? anchorPageId;
  const anchorNote = currentId === null ? null : (notes.current.get(currentId) ?? null);
  const nextRow = useMemo(() => {
    if (currentId === null) return null;
    const i = pages.findIndex((p) => p.id === currentId);
    return i < 0 ? null : (pages[i + 1] ?? null);
  }, [pages, currentId]);
  const nextNote = nextRow === null ? null : (notes.current.get(nextRow.id) ?? null);

  /**
   * The state the playback bar renders, and it is the state of whichever page the listener is
   * WAITING for — not the one that is playing.
   *
   * While the engine is `waiting`, the page on the deck is finished and perfectly `ready`; reporting
   * that would show "ready" over the stall copy the reader is looking at. §4.9's whole "generating the
   * next page — about 12 seconds" line is about the page that does not exist yet.
   */
  const waiting = status === "waiting";
  const shown = waiting ? nextNote : anchorNote;
  const state: PageState =
    waiting
      ? (shown?.state ?? "building")
      : status === "error"
        ? "failed"
        : // The page on the deck IS this page, and it decoded — nothing the planner could say would
          // make that less true.
          enginePage !== null && enginePage.audio.pageId === currentId
          ? "ready"
          : (shown?.state ?? "missing");

  const overdue =
    shown !== null && shown.etaSec !== null && shown.state !== "ready"
      ? Date.now() - shown.askedAt > shown.etaSec * 1000 + 5_000
      : false;

  /**
   * **Which page `state` is about**, and the two numbers that go with it.
   *
   * ⚠️ Added in P5's integration pass because §7.5's whole blocking path was unreachable without it.
   * `useRoomSync` reports `{t:"notready", reason:"generating", pageId, etaSec}` on this member's
   * behalf, and `server/src/rooms/ws.ts` opens that handler with `if (!pageId) return;` — a report
   * with no page is dropped on the floor without a reply, correctly, because `admitGeneratingBlock`
   * has to `planPage()` the page before it may believe anybody. `Room.tsx` had nowhere to get one, so
   * it passed `pageId: null` and **no browser has ever installed a `generating` block**: the room
   * never paused for a page that was still being made, and nothing anywhere disagreed — the member
   * saw their own stall copy, everyone else saw a room that had simply stopped.
   *
   * It is deliberately the id of the page `state` describes and not `page?.pageId`: while the engine
   * is `waiting`, the page on the deck is finished and `ready`, and the wait is about the NEXT one.
   * Sending the deck's id would block the room on a page that already exists — which the server would
   * refuse as `nothing-to-do`, i.e. the same silence with more steps.
   */
  const waitingPageId = waiting ? (nextRow?.id ?? null) : currentId;

  const frames = useCallback(
    (tick: () => void) => {
      if (engine === null) return () => undefined;
      // The engine's loop, adapted to the driver's bare tick. Never a second rAF — see the header.
      return engine.subscribe((ev) => {
        if (ev.type === "tick") tick();
      });
    },
    [engine],
  );

  const chapter = useMemo(
    () =>
      enginePage === null
        ? null
        : { pageOffsetMs: enginePage.chapterOffsetSec * 1000, durationMs: enginePage.chapterDurationSec * 1000 },
    [enginePage],
  );

  return useMemo(() => {
    if (engine === null || book === null) return null;
    return {
      page: enginePage?.audio ?? null,
      clock: engine,
      playing: status === "playing",
      frames,
      state,
      pendingChars: shown?.missingChars ?? null,
      /** The page `state` is about. See `waitingPageId` above — §7.5 cannot report a block without it. */
      waitingPageId,
      /** The planner's own estimate for that page, or null. LAW 9: null is "not measured", never 0. */
      etaSec: shown?.etaSec ?? null,
      /** When this wait began. The clock of the WAIT, not of the poll — `PageNote.askedAt`. */
      waitStartedAt: shown?.askedAt ?? null,
      /**
       * **The engine has run out of page**, whatever the planner thinks. §7.5's `generating` report
       * is driven off this rather than off `state`, because `ensure` fails fast when spark-1 is
       * unreachable — nothing is ever `queued`, the plan says `missing`, and a report keyed on the
       * plan's string is never sent, so the server is never asked and its refusal branch never
       * speaks. `waiting` is a fact about this device and is true in every one of those cases.
       */
      waiting,
      /** The planner's reason for not being ready, so `WaitingBanner` can name it. */
      blocked: shown?.blocked ?? null,
      /** `buffering`: the engine is fetching or decoding and is not playing. §7.5's first row. */
      stalled: status === "loading",
      overdue,
      chapter,
      voice: book.voiceLabel,
      onPlayPause,
      onRate,
      onSeek,
      onSeekChapterMs: seekChapterMs,
      onNarrate,
      onRetry,
      onAnchor,
      onPlayFrom,
      onChanged,
      // A room routes every control through itself; these would move one member and not the rest.
      listen: inRoom ? null : listen,
    };
  }, [
    listen,
    inRoom,
    engine,
    book,
    enginePage,
    status,
    frames,
    state,
    shown?.missingChars,
    shown?.etaSec,
    shown?.askedAt,
    waitingPageId,
    waiting,
    overdue,
    chapter,
    onPlayPause,
    onRate,
    onSeek,
    seekChapterMs,
    onNarrate,
    onRetry,
    onAnchor,
    onPlayFrom,
    onChanged,
  ]);
}

/** Is this page part of a book this browser saved for offline, in the edition on screen (LAW 1)? */
async function isSavedOffline(b: NarrationBook, pageId: string): Promise<boolean> {
  try {
    const saved = await getOfflineBook(b.bookId);
    return saved !== null && saved.editionId === b.editionId && saved.pages.some((p) => p.pageId === pageId);
  } catch {
    return false;
  }
}

/** The worker's stored copy of a page's plan. `needAudio`: only a plan that can actually play. */
async function cachedPlan(url: string, needAudio: boolean): Promise<EnsureBody | null> {
  const hit = await cachedBookResponse(url);
  if (hit === null) return null;
  try {
    const body = (await hit.json()) as EnsureBody;
    if (needAudio && !validatePageAudio(body.audio).ok) return null;
    return body;
  } catch {
    return null;
  }
}
